```vue
<template>   
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
  >
    <!-- BURGUNDY AND GOLD ELEGANT ALERT -->
    <transition name="fade">
      <div v-if="showAlert && !asistenciaConfirmada && !showQRCode" class="custom-alert-overlay" @click.self="cerrarAlerta">
        <div class="custom-alert-card">
          <div class="alert-icon">✨</div>
          <h3 class="alert-title">RSVP Confirmation</h3>
          <p class="alert-message">
            We kindly ask you to confirm your attendance as soon as possible, in order to coordinate 
            the details of the event with the excellence you deserve.
          </p>
          <button class="alert-button" @click="cerrarAlerta">Understood</button>
        </div>
      </div>
    </transition>

    <div 
      class="confirmation-card"
      ref="formCardRef"
    >
      <!-- CARD INNER CONTENT -->
      <div class="card-body-content">
        
        <!-- DIGITAL PASS VIEW WITH QR CODE / FINAL MESSAGE -->
        <div v-if="asistenciaConfirmada || showQRCode" class="success-view text-center">
          <div v-if="attending === 'no'" class="no-assistance-view">
            <p class="success-message">
              Thank you for letting us know. We are very sorry you won't be able to join us.
            </p>
          </div>
          <div v-else class="qr-view">
            <p class="qr-header">Digital Pass</p>
            <div class="qr-wrapper">
              <img v-if="qrCode" :src="qrCode" alt="QR Code" class="qr-code">
              <p v-else class="qr-error-message">The QR code has not been generated</p>
            </div>
            <p class="qr-instructions">Present it on the day of the event</p>
          </div>
        </div>

        <!-- STEP-BY-STEP FORM -->
        <div v-else class="form-wrapper">
          <div class="form-header text-center">
            <h2 ref="tituloAnimado" class="form-title">   <br>

              Confirm Attendance
            </h2>
            <p class="rsvp-deadline"></p>

            <div class="progress-container">
              <div
                class="progress-bar"
                :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
              ></div>
            </div>
            <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>
          </div>

          <form @submit.prevent="handleSubmit" class="confirmation-form">
            <!-- Step 1 -->
            <div v-if="currentStep === 1" class="step-box">
              <div class="form-group">
                <label class="form-label">Name</label>
                <input
                  v-model.trim="name"
                  type="text"
                  class="form-control"
                  placeholder="Enter your name"
                  required
                >
              </div>
              <div class="form-group">
                <label class="form-label">Phone</label>
                <input
                  v-model.trim="phone"
                  type="tel"
                  class="form-control"
                  placeholder="10-digit mobile number"
                  required
                  pattern="[0-9]{10}"
                  title="Enter a 10-digit number"
                >
              </div>
            </div>

            <!-- Step 2 (Only for non-XV / YM events) -->
            <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
              <div class="form-group">
                <label class="form-label text-center d-block">Invited by?</label>
                <div class="radio-group">
                  <label><input type="radio" value="novia" v-model="invitedBy" required> Bride</label>
                  <label><input type="radio" value="novio" v-model="invitedBy"> Groom</label>
                </div>
              </div>
            </div>

            <!-- Attendance Step -->
            <div v-if="currentStep === asistenciaStep" class="step-box">
              <label class="form-label text-center d-block">Will you attend?</label>
              <div class="radio-group">
                <label><input type="radio" value="si" v-model="attending" required> Yes</label>
                <label><input type="radio" value="no" v-model="attending"> No</label>
              </div>
            </div>

            <!-- Passes / Number of attendees step -->
            <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
              <label class="form-label text-center d-block">How many will attend?</label>
              <select v-model.number="guests" class="form-select" :disabled="loading" required>
                <option :value="null" disabled>Select an option</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <div v-if="loading" class="loading-indicator text-center mt-1">Loading passes...</div>
            </div>

            <!-- Buttons -->
            <div class="d-flex justify-content-between mt-3 buttons-row gap-2">
              <button
                v-if="currentStep > 1"
                type="button"
                class="submit-button secondary"
                @click="prevStep"
                :disabled="isSubmitting"
              >
                Back
              </button>

              <button
                v-if="currentStep < totalSteps"
                type="button"
                class="submit-button"
                @click="nextStep"
                :disabled="isSubmitting"
              >
                Next
              </button>

              <button
                v-if="currentStep === totalSteps"
                type="submit"
                class="submit-button"
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting">Confirm</span>
                <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </button>
            </div>

            <div v-if="errorMessage" class="alert alert-danger mt-2 text-center">
              {{ errorMessage }}
            </div>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '@/api'
import gsap from 'gsap'
import Swal from 'sweetalert2'

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: {
      type: [Number, String],
      required: true
    },
    nombre: {
      type: String,
      default: ''
    },
    idFestejado: {
      type: Number,
      default: null
    }
  },
  setup(props) {
    const name = ref('')
    const phone = ref('')
    const invitedBy = ref('')
    const attending = ref('')
    const guests = ref(null)
    const guestsOptions = ref([1, 2])
    const loading = ref(true)
    const isSubmitting = ref(false)
    const errorMessage = ref('')

    const qrCode = ref(null)
    const showQRCode = ref(false)
    const asistenciaConfirmada = ref(false)
    const currentStep = ref(1)

    const formCardRef = ref(null)
    const tituloAnimado = ref(null)
    const containerRef = ref(null)

    // Alert Control
    const showAlert = ref(false)
    const alertShown = ref(false)
    let observer = null

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase()
      return ['xv', 'ym'].includes(eventName)
    })

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4)
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3)

    const cerrarAlerta = () => {
      showAlert.value = false
    }

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box')
        if (!box) return
        gsap.fromTo(
          box,
          { y: 30, opacity: 0, transformPerspective: 300, rotationX: -10 },
          { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', rotationX: 0 }
        )
      })
    }

    const nextStep = async () => {
      errorMessage.value = ''

      // Step 1: Name and phone
      if (currentStep.value === 1) {
        if (!name.value.trim() || !phone.value.trim()) {
          errorMessage.value = 'Please enter your name and phone number'
          return
        }
      }

      // Step 2 (non-XV): Invited by
      if (currentStep.value === 2 && !isXvEvent.value) {
        if (!invitedBy.value) {
          errorMessage.value = 'Please select who invited you'
          return
        }
      }

      // Attendance Step
      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Please select whether you will attend or not'
          return
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value
          await nextTick()
          handleSubmit()
          return
        }
      }

      // Final Step: guests
      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Please select how many people will attend'
          return
        }
      }

      if (currentStep.value < totalSteps.value) {
        currentStep.value++
        animateStep()
      }
    }

    const prevStep = () => {
      errorMessage.value = ''
      if (currentStep.value > 1) {
        currentStep.value--
        animateStep()
      }
    }

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        )
        const numPases = Number(response.data?.num_pase)
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2]
      } catch (error) {
        console.error('Error fetching guest limit:', error)
        errorMessage.value = 'Error loading guest options'
      } finally {
        loading.value = false
      }
    }

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: Number(props.idInvitado)
        })
        if (response.data?.asistio) {
          asistenciaConfirmada.value = true
          await obtenerCodigoQR()
        }
      } catch (error) {
        console.error('Error checking attendance:', error)
      }
    }

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, {
          id_invitado: Number(props.idInvitado)
        })
        qrCode.value = response.data?.codigoqr || null
      } catch (error) {
        console.error('Error obtaining QR code:', error)
        errorMessage.value = 'Error generating the QR code'
      }
    }

    const handleSubmit = async () => {
      if (attending.value === 'no') guests.value = 0

      isSubmitting.value = true
      errorMessage.value = ''

      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: Number(props.idInvitado),
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0))
        }

        await axios.post(`${baseUrl}/asistencia`, formData)

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: 'Thank you for letting us know!',
            text: "We are very sorry you won't be able to attend.",
            confirmButtonColor: '#8C1C29',
          })
          asistenciaConfirmada.value = true
        } else {
          await obtenerCodigoQR()
          showQRCode.value = true
          asistenciaConfirmada.value = true
        }
      } catch (error) {
        console.error('Error confirming attendance:', error)
        errorMessage.value = 'There was an error confirming. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }

    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = 0
      } else if (newValue === 'si' && (guests.value === 0 || guests.value == null)) {
        guests.value = 1
      }
    })

    onMounted(async () => {
      await nextTick()

      fetchGuestLimit()
      verificarAsistencia()

      gsap.fromTo(
        formCardRef.value,
        { opacity: 0, y: 30, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power2.out' }
      )

      gsap.fromTo(
        tituloAnimado.value,
        { y: -15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      )

      if (containerRef.value) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !alertShown.value) {
                showAlert.value = true
                alertShown.value = true
              }
            })
          },
          { threshold: 0.3 }
        )
        observer.observe(containerRef.value)
      }
    })

    onUnmounted(() => {
      if (observer) {
        observer.disconnect()
      }
    })

    return {
      name,
      phone,
      invitedBy,
      attending,
      guests,
      guestsOptions,
      loading,
      isSubmitting,
      errorMessage,
      isXvEvent,
      asistenciaConfirmada,
      qrCode,
      showQRCode,
      currentStep,
      totalSteps,
      asistenciaStep,
      nextStep,
      prevStep,
      handleSubmit,
      formCardRef,
      tituloAnimado,
      containerRef,
      showAlert,
      cerrarAlerta
    }
  }
}
</script>

```
<style scoped>
/* ESTILOS DE LA ALERTA PERSONALIZADA (TINTO / CHIC) */
.custom-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 110vh;
  background: rgba(15, 5, 8, 0.65);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  margin-bottom: 50px;;
}

.custom-alert-card {
  background: #d6cabd;
;
  border: 1px solid rgba(212, 175, 55, 0.4);
  border-radius: 16px;
  padding: 1.8rem 1.5rem;
  max-width: 280px;
  width: 80%;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  animation: popIn 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.alert-icon {
  font-size: 1.8rem;
  margin-bottom: 0.4rem;
}

.alert-title {
  font-family: 'Playfair Display', serif;
  color: #53442e;
  font-size: 1.35rem;
  margin-bottom: 0.8rem;
  letter-spacing: 0.5px;
}

.alert-message {
  font-family: 'quote', sans-serif;
  color: #363433;
  font-size: 0.92rem;
  line-height: 1.5;
  
  margin-bottom: 1.4rem;
}

.alert-message strong {
  color: #ffd700;
  font-weight: 600;
}

.alert-button {
background: linear-gradient(
  135deg,
  #f0c9d3 0%,
  #f5f7fa 25%,
  #ffffff 50%,
  #f0c9d3 75%,
  #f0c9d3 100%
);  color: #2b060a;
  font-family: 'Karla', sans-serif;
  font-weight: 700;
  border: none;
  border-radius: 25px;
  padding: 0.55rem 1.8rem;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.alert-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(191, 149, 63, 0.4);
}

@keyframes popIn {
  0% { opacity: 0; transform: scale(0.85) translateY(10px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

/* Transición Vue Fade */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* CONTENEDOR CONTENIDO */
.confirmation-container {
  width: 100%;
  padding: 1rem 0.5rem;
  box-sizing: border-box;
}

/* TARJETA MARCO AZUL */
/* TARJETA MARCO AZUL */
.confirmation-card {
  width: 100%;
  margin-bottom: 50px;
  max-width: 340px !important; /* Ajuste ligero para dar aire a la pantalla */
  min-height: 650px;
  background: url('@/assets/Diseño sin título (78).png') no-repeat center center;
  background-size: 100% 100%;
  /* AUMENTO RADICAL DEL PADDING LATERAL: de 45px a 75px */
  /* Esto empuja todo el contenido hacia el centro */
  padding: 195px 75px 45px !important; 
  border-radius: 16px;
  box-sizing: border-box;
  position: relative; /* Importante para asegurar el posicionamiento interno */
}

.card-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

}

.form-wrapper {
  width: 100%;
}

/* ENCABEZADOS Y TÍTULOS */
.form-header {
  margin-bottom: 0.8rem;
  margin-top:-90px;
}

.form-title {
  font-family: 'CynthiaJuneJF', cursive;
  margin-top:-10px;
  font-size: 40px;
  background: linear-gradient(110deg, #a67c00 30%, #bf953f 45%, #ffffff 50%, #bf953f 55%, #a67c00 70%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.3));
  display: block; 
}

.rsvp-deadline {
  font-family: 'Playfair Display', serif;
  font-size: 10.5px;
  color: rgba(81, 67, 45, 0.95);
  margin: 0 0 8px;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color:#ee93bd;
  font-size: 10px;
  margin-top: 3px;
}

/* BARRA DE PROGRESO COMPACTA */
.progress-container {
  height: 4px;
  background-color: rgba(110, 101, 64, 0.3);
  border-radius: 4px;
  overflow: hidden;
  width: 75%;
  margin: 0 auto;
}

.progress-bar {
  height: 100%;
  background-color: #89844c;
  transition: width 0.4s ease;
}

/* CAMPOS DE FORMULARIO - REDUCIDOS EN ANCHO */
.form-group {
  margin-bottom: 0.65rem;
  text-align: left;
}

.form-label {
  display: block;
  margin-bottom: 0.2rem;
  padding-right:30px !important ;
  font-family: 'Karla', sans-serif;
  font-size: 19px !important;
  font-weight: 600;
  color: #2c2626;
}

.form-control,
.form-select {
  width: 100%;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0.4rem;
  background-color: rgba(255, 255, 255, 0.95);
  color: #333333;
  box-sizing: border-box;
}

.form-control:focus,
.form-select:focus {
  border-color: #ee93bd;
  box-shadow: 0 0 0 0.18rem rgba(191, 149, 63, 0.3);
  outline: none;
}

/* OPCIONES SÍ/NO */
.radio-group {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  padding: 0.2rem 0;
  color: #2a2929;
  font-family: 'Karla', sans-serif;
  font-size: 13px;
}

/* BOTONES CON TEXTURA DORADA */
.submit-button {
  width: 100%;
  padding: 0.5rem 0.65rem;
  font-family: 'Playfair Display', serif;
  font-size: 1.15rem;
  font-weight: 800;
background-image: linear-gradient(
  135deg,
  rgba(255, 255, 255, 0.65),
  rgba(220, 230, 242, 0.55),
  rgba(180, 195, 215, 0.45)
), url('@/assets/dorado.jpeg');

background-size: cover;  background-size: cover;
  background-position: center;
  background-color: #bf953f;
  color: #1d1c1c !important;
  -webkit-text-fill-color: #1d1c1c !important;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

.submit-button.secondary {
  background: rgba(255, 255, 255, 0.85) !important;
  color: #333333 !important;
  -webkit-text-fill-color: #333333 !important;
}

/* MENSAJE DE CONFIRMACIÓN FINAL */
.qr-header {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  color: #1d1c1c;
  margin-bottom: 0.4rem;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: rgba(37, 36, 36, 0.9);
  font-size: 0.85rem;
}

.alert {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 0.4rem;
}

@media (max-width: 576px) {
  .confirmation-card {
    padding: 175px 36px 36px;
    min-height: 580px;
  }

  .form-title {
  }
}
</style>